import { useCallback, useEffect, useMemo, useState } from "react"
import { GetApp } from "../App"
import { logger } from "../App/Logger"
import { GetShipContext } from "../App/Context"
import { useSubscription } from "../App/useSubscription"
import { Icons } from "./Icon"
import { MenuItemBlank, MenuItemToggleState } from "./Menu"
import { MenuStatusButton, type MenuStatusInfo } from "./MenuStatusButton"
import { ApiClient, SyncableApi, IResponse, ILogbookAppSyncStatusResult, ILogbookAppStatusResult, ILogbookAppInternetStatus, ILogbookSyncStatus } from "../Types"
import { Subscription } from "../Types/Generated/LogbookApi/Subscriptions"
import { GetShipView } from "../Views/ShipView"
import { GetAppView } from "../Views/AppView"
import { AppOverlayContent } from "../Views/App/OverlayContentView"
import { ShipViewOverlayContent } from "../Views/ShipOverlayContentView"

export const MenuSyncData = {
  updateSync(api: ApiClient, enabled: boolean): Promise<IResponse> {
    return new SyncableApi(api).sync.update({ params: {}, body: { enabled: enabled } }).call();
  },
};

export interface MenuSyncProps {
  // Opt-in initial open state, forwarded to the underlying `MenuStatusButton`.
  // Real callers never set this; it exists so catalog fixtures can preview the
  // item list itself, not just the collapsed button (#384).
  initOpen?: boolean;
}

function InternetStatus(): ILogbookAppInternetStatus | undefined {
  return window.navigator.onLine ? ILogbookAppInternetStatus.connected : ILogbookAppInternetStatus.failed;
}

export function MenuSync({ initOpen }: MenuSyncProps) {
  const app = GetApp();
  const { apiUser } = GetShipContext();
  const syncUpdateAvailable = new SyncableApi(app.apiClient).sync.update({ params: {}, body: {} }).available(apiUser);
  const { setShipOverlay } = GetShipView();
  const { setAppOverlay } = GetAppView();
  const [lastSync, setLastSync] = useState<number | undefined>(undefined);
  const [syncStatus, setSyncStatus] = useState<ILogbookAppSyncStatusResult | undefined>(app.status?.sync);
  const [internetStatus, setInternetStatus] = useState<ILogbookAppInternetStatus | undefined>(() =>
    app.staticLayout ? app.status?.internet ?? InternetStatus() : InternetStatus());
  const t = app.getT("MenuSync");

  useEffect(() => {
    if (app.status && app.status.sync && (!lastSync || !app.status.date || lastSync < app.status.date)) {
      setSyncStatus(app.status.sync);
      setLastSync(app.status.date);
    }
  }, [app.status]);

  useEffect(() => {
    const updateInternetStatus = () => {
      setInternetStatus(InternetStatus());
    };
    window.addEventListener("online", updateInternetStatus);
    window.addEventListener("offline", updateInternetStatus);
    return () => {
      window.removeEventListener("online", updateInternetStatus);
      window.removeEventListener("offline", updateInternetStatus);
    };
  }, []);

  useSubscription((status: ILogbookAppStatusResult, _type: string) => {
    if (status.sync) {
      setSyncStatus(status.sync);
      setLastSync(new Date().getTime());
    }
  }, Subscription.AppStatus);

  const refreshStatus = useCallback(async () => {
    try {
      const next = await app.updateStatus();
      if (next.sync) {
        setSyncStatus(next.sync);
        setLastSync(new Date().getTime());
      }
    } catch (error) {
      // Keep the last known status visible; the menu remains usable offline.
      logger.debug("sync menu status refresh failed", error);
    }
  }, [app]);

  const disconnected = useMemo(() => {
    return syncStatus?.status == ILogbookSyncStatus.disconnected;
  }, [syncStatus]);

  // The cloud has stopped accepting this app's session (#1449). Distinct from
  // `disconnected`, which is about a ship claim: nothing here can be fixed by
  // resyncing, only by logging in again.
  const sessionExpired = useMemo(() => {
    return syncStatus?.status == ILogbookSyncStatus.sessionExpired;
  }, [syncStatus]);

  const internetStatusInfo = useMemo(() => {
    if (app.cloudSession()) {
      return undefined;
    }

    switch (internetStatus) {
      case ILogbookAppInternetStatus.connected:
        return { icon: Icons.Cloud, class: "ok" };
      default:
        return { icon: Icons.NoCloud, class: "error" };
    }
  }, [internetStatus]);

  const syncStatusClass = useMemo(() => {
    switch (syncStatus?.status) {
      case ILogbookSyncStatus.current:
        if (internetStatus == ILogbookAppInternetStatus.connected) {
          if (syncStatus.enabled) {
            return "ok";
          }
          return "error";
        } else {
          if (syncStatus.enabled) {
            return "neutral";
          }
          return "error";
        }
      case ILogbookSyncStatus.disconnected:
        return "error";
      // Red, not the neutral "something is in flight" the default arm would give it:
      // nothing will sync again until the user logs back in (#1449).
      case ILogbookSyncStatus.sessionExpired:
        return "error";
      case ILogbookSyncStatus.inProgress:
      case ILogbookSyncStatus.paused:
      case ILogbookSyncStatus.failed:
        if (internetStatus == ILogbookAppInternetStatus.connected) {
          if (syncStatus.enabled) {
            return "neutral";
          }
          return "error";
        } else {
          if (syncStatus.enabled) {
            return "neutral";
          }
          return "error";
        }
      default:
        return "neutral";
    }
  }, [internetStatus, syncStatus]);

  const syncMenuInfo = useMemo<MenuStatusInfo>(() => {
    if (internetStatus == ILogbookAppInternetStatus.failed && syncStatus?.enabled) {
      return { icon: Icons.NoCloud, class: "ShipView-status-neutral", surface: "outlined" };
    } else if (syncStatus?.status == ILogbookSyncStatus.sessionExpired) {
      // Ahead of the `enabled` arms below: sync is still switched on, it just cannot
      // reach the cloud with a session the cloud refuses (#1449).
      return { icon: Icons.BoxFilled, class: "ShipView-status-error", surface: "filled" };
    } else if (syncStatus?.status == ILogbookSyncStatus.disconnected) {
      return { icon: internetStatus == ILogbookAppInternetStatus.connected ? Icons.BoxFilled : Icons.NoCloud, class: "ShipView-status-error", surface: internetStatus == ILogbookAppInternetStatus.connected ? "filled" : "outlined" };
    } else if (internetStatus == ILogbookAppInternetStatus.failed && !syncStatus?.enabled) {
      return { icon: Icons.NoCloud, class: "ShipView-status-error", surface: "outlined" };
    } else if (syncStatus?.enabled && syncStatus?.status == ILogbookSyncStatus.current) {
      return { icon: Icons.BoxFilled, class: "ShipView-status-ok", surface: "filled" };
    } else if (syncStatus?.enabled && syncStatus?.status !== ILogbookSyncStatus.current) {
      return { icon: Icons.BoxFilled, class: "ShipView-status-neutral", surface: "filled" };
    }
    return { icon: Icons.Box, class: "ShipView-status-error", surface: "outlined" };
  }, [internetStatus, syncStatus]);

  const syncStatusToggle = useMemo(() => {
    return syncStatus?.enabled ? MenuItemToggleState.on : MenuItemToggleState.off;
  }, [syncStatus]);

  if (app.cloudSession() || !syncStatus) {
    return;
  }

  return (
    <MenuStatusButton
      info={syncMenuInfo}
      title={t("title")}
      buttonClass="button MenuSync-button"
      global={true}
      initOpen={initOpen}
      onClick={(open) => {
        if (open) {
          void refreshStatus();
        }
      }}
      items={[
      // Option internet-status
      // missing the functions which determines the internet status
        !internetStatusInfo ? MenuItemBlank :
            {
              id: "internet-status",
              icon: internetStatusInfo.icon,
              iconClass: `ShipView-status-${internetStatusInfo.class}`,
              title: t("internet_status"),
            },

        // Option sync-status
        sessionExpired ? {
          // Neither the sync toggle nor a resync can help: the cloud is refusing this
          // app's session, so the only useful action is logging in again (#1449).
          id: "relogin",
          onClick: async () => {
            setAppOverlay(AppOverlayContent.sessionExpired);
            return true;
          },
          icon: Icons.Refresh,
          iconClass: `ShipView-status-error`,
          title: t("relogin"),
        } :
          disconnected ? {
            id: "resync",
            onClick: syncUpdateAvailable ? async () => {
              setShipOverlay(ShipViewOverlayContent.resync);
              return true;
            } : undefined,
            icon: Icons.Refresh,
            iconClass: `ShipView-status-error`,
            title: t("resync"),
          } :
              {
              // disabled: internetStatus !== ILogbookAppInternetStatus.connected,
                id: "sync-status",
                onClick: syncUpdateAvailable ? async () => {
                  const _response = await MenuSyncData.updateSync(app.apiClient, !syncStatus?.enabled);
                  return true;
                } : undefined,
                icon: Icons.CircleFilledLarge,
                iconClass: `ShipView-status-${syncStatusClass}`,
                title: t("status"),
                toggle: syncUpdateAvailable ? syncStatusToggle : undefined,
              },
      // Option sync_last_update
      // syncStatus.lastSync ? {id: "sync-last-update", title: t("last_update", {timestamp: app.format("timeAgoDate", syncStatus.lastSync)})} : MenuItemBlank,
      ]}
    />
  );
}
